import React, { useMemo, useState } from "react";
import PersonLink from "../../components/PersonLink";
import { Row, Container, Button, Stack } from "react-bootstrap";
import { SemesterName, CourseName } from "./styles";
import { StyledTitle } from "../../styles";
import { Switch, Route, useRouteMatch } from "react-router-dom";
import { Helmet } from "react-helmet";

const Semester = ({ semester, people }) => {
  return (
    <>
      <Row>
        <SemesterName>{semester[0]}</SemesterName>
      </Row>
      {semester[1].map((course, index) => (
        <Row key={index}>
          <CourseName>
            {course.courseLink ? (
              <a href={course.courseLink}>{course.courseName}</a>
            ) : (
              course.courseName
            )}{" "}
            {course.instructor !== "" ? "—" : ""}{" "}
            <PersonLink name={course.instructor} people={people} />
          </CourseName>
        </Row>
      ))}
    </>
  );
};

const TeachingList = ({ teaching, people }) => {
  return Object.entries(teaching).map((pair) => (
    <Container key={pair[0]}>
      <hr />
      <Row>
        <StyledTitle>{pair[0]}</StyledTitle>
      </Row>
      {pair[1].map((semesters) =>
        Object.entries(semesters).map((semester, index) => (
          <Semester key={index} semester={semester} people={people} />
        )),
      )}
    </Container>
  ));
};

const copyToClipboard = async (text) => {
  await navigator.clipboard.writeText(text);
};

const AddEditTeaching = ({ teaching }) => {
  const [outputJson, setOutputJson] = useState(null);
  const [agree, setAgree] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedKey, setSelectedKey] = useState(""); // `${cat}||${sem}||${name}`
  const [prefill, setPrefill] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const categories = Object.keys(teaching || {});

  const options = useMemo(() => {
    const list = [];
    Object.entries(teaching || {}).forEach(([cat, arr]) => {
      (arr || []).forEach((semObj) => {
        Object.entries(semObj || {}).forEach(([sem, courses]) => {
          (courses || []).forEach((c) => {
            list.push({
              key: `${cat}||${sem}||${c.courseName}`,
              label: `${c.courseName} — ${sem} — ${cat}`,
              cat,
              sem,
              course: c,
            });
          });
        });
      });
    });
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [teaching]);

  function switchMode(next) {
    setMode(next);
    if (next === "add") {
      setPrefill(null);
      setSelectedKey("");
      setFormKey((k) => k + 1);
    }
  }

  function handleSelectExisting(e) {
    const key = e.target.value;
    setSelectedKey(key);
    const found = options.find((o) => o.key === key);
    if (!found) {
      setPrefill(null);
      return;
    }
    const { cat, sem, course } = found;
    setPrefill({
      category: cat,
      semester: sem,
      courseName: course.courseName || "",
      courseLink: course.courseLink || "",
      instructor: course.instructor || "",
      previousCategory: cat,
      previousSemester: sem,
      previousCourseName: course.courseName || "",
    });
    setFormKey((k) => k + 1);
  }

  function handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const raw = Object.fromEntries(formData.entries());

    const category = raw.category || "Current Offerings";
    const semesterLabel = (raw.semester || "").trim();
    const course = {
      courseName: raw.courseName || "",
      courseLink: raw.courseLink || "",
      instructor: raw.instructor || "",
    };

    const payload =
      mode === "edit"
        ? {
            action: "update",
            previousCategory: prefill?.previousCategory || category,
            previousSemester: prefill?.previousSemester || semesterLabel,
            previousCourseName: prefill?.previousCourseName || course.courseName,
            category,
            semester: semesterLabel,
            course,
          }
        : {
            category,
            semester: semesterLabel,
            course,
          };

    setOutputJson(payload);
  }

  const jsonString = outputJson ? JSON.stringify(outputJson, null, 2) : "";

  return (
    <div>
      <h3>Add or edit a course</h3>
      <Helmet>
        <title>Add or edit a course - Khoury Vis Lab</title>
        <meta name="description" content="Add or edit a course" />
      </Helmet>

      <div style={{ marginBottom: 12 }}>
        <label>
          <input type="radio" name="mode" value="add" checked={mode === 'add'} onChange={() => switchMode('add')} />{' '}Add
        </label>{' '}
        <label style={{ marginLeft: 16 }}>
          <input type="radio" name="mode" value="edit" checked={mode === 'edit'} onChange={() => switchMode('edit')} />{' '}Edit
        </label>
      </div>

      {mode === "edit" && (
        <div style={{ marginBottom: 12 }}>
          <label>
            <strong>Select existing course</strong>
            <br />
            <select value={selectedKey} onChange={handleSelectExisting}>
              <option value="">-- choose --</option>
              {options.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <form key={formKey} method="post" onSubmit={handleSubmit}>
        <hr />
        <h4>Required metadata</h4>
        <p>
          <label>
            <strong>Category</strong>
            <br />
            <select name="category" defaultValue={prefill?.category}>
              {categories.map((c) => (
                <option value={c} key={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </p>
        <p>
          <label>
            <strong>Semester label</strong> — e.g., "Fall 2025"
            <br />
            <textarea name="semester" rows={1} cols={120} defaultValue={prefill?.semester || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Course Name</strong>
            <br />
            <textarea name="courseName" rows={1} cols={120} defaultValue={prefill?.courseName || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Course Link</strong>
            <br />
            <textarea name="courseLink" rows={1} cols={120} defaultValue={prefill?.courseLink || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Instructor</strong>
            <br />
            <textarea name="instructor" rows={1} cols={120} defaultValue={prefill?.instructor || ''} />
          </label>
        </p>

        <hr />
        <h4>Submitting</h4>
        <label>
          <input
            type="checkbox"
            defaultChecked={false}
            checked={agree}
            onChange={() => setAgree(!agree)}
          />
          {' '}I have filled required fields and checked for accuracy.
        </label>
        <Button type="submit" className="mt-3 d-block" disabled={!agree}>
          Step 1: Generate JSON
        </Button>
      </form>

      {outputJson && (
        <div>
          <Stack direction="horizontal" gap={3} className="mt-2">
            <Button onClick={() => copyToClipboard(jsonString)}>
              Step 2: Copy to clipboard
            </Button>
            <Button
              className="mt-2"
              onClick={() => {
                const isEdit = mode === 'edit';
                const baseUrl = isEdit
                  ? "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=update_teaching&projects=&title=[UPDATE COURSE] "
                  : "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=new_teaching&projects=&template=new-teaching-template.md&title=[NEW COURSE] ";
                const title = encodeURIComponent(outputJson.course.courseName || "");
                const url = baseUrl + title;
                window.open(url);
              }}
            >
              Step 3: Paste into new issue
            </Button>
          </Stack>
          <pre>{jsonString}</pre>
        </div>
      )}
    </div>
  );
};

const Teaching = ({ teaching, people }) => {
  let { path } = useRouteMatch();
  return (
    <Switch>
      <Route exact path={path}>
        <TeachingList teaching={teaching} people={people} />
      </Route>
      <Route path={`${path}/add`}>
        <AddEditTeaching teaching={teaching} />
      </Route>
    </Switch>
  );
};

export default Teaching;
